import React, { useEffect, useState } from 'react';
import styled, { ThemeProvider, css } from 'styled-components';
import { ActionHandler, setExportHTMLMapMode, setMapInfo } from '@kepler.gl/actions';
import { MapInfo } from '@kepler.gl/types';
import { injectIntl } from 'react-intl';
import { IntlShape } from 'react-intl';
import ShareMapInstanceFactory from './share-map-instance';

import {
  StyledExportSection,
  LoadingDialog
} from '@kepler.gl/components';

import {
  MAP_INFO_CHARACTER
} from '@kepler.gl/constants';
import axios from 'axios';

const host = process.env.API_URL;

const StyledModal = styled.div`
  min-height: 50vh;
  overflow: hidden;
  display: flex;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 8px;
    text-align: left;
  }
`;

const StyledTableRow = styled.tr`
  &:nth-child(odd) {
    background-color: #f4f4f4; /* Shading for alternate rows */
  }
  &:hover {
    font-weight: bold;
    background-color: lightgray;
    cursor: pointer;
  }
`;

const StyledNewMapRow = styled.tr`
  background-color: lightgreen; /* Set the background color to green */
  font-weight: bold;
  &:hover {
    background-color: green;
    cursor: pointer;
    color: white; /* Set the text color to white for better visibility */
  }
`;

const StyledTableCell = styled.td`
  padding: 8px;
  text-align: left; /* Left justify text */
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledInputLabel = styled.label`
  font-size: 12px;
  color: ${props => props.theme.textColorLT};
  letter-spacing: 0.2px;
`;

const StyledInnerDiv = styled.div`
  min-height: 500px;
  width: 100%;
`;

type CharacterLimits = {
  title?: number;
  description?: number;
};

interface ShareMapManagerFactoryProps {
  mapInfo: MapInfo;
  characterLimits?: CharacterLimits;
  onChangeExportMapHTMLMode: ActionHandler<typeof setExportHTMLMapMode>;
  generateUrl: () => Promise<string>;
  onSetMapInfo: typeof setMapInfo;
  options: {
    mode?: string;
  };
}

type IntlProps = {
  intl: IntlShape;
};

export interface MapObject {
  id: string;
  title: string;
  description: string;
  share_type: string;
  password: string;
  creation_time: string;
  enabled: boolean;
}

export interface LoadingMethod {
  id: string;
  label: string;
  elementType: React.ComponentType<any>;
  tabElementType?: React.ComponentType<{ onClick: React.MouseEventHandler; intl: IntlShape }>;
}

ShareMapManagerFactory.deps = [ShareMapInstanceFactory]

export default function ShareMapManagerFactory(
  ShareMapInstance: ReturnType<typeof ShareMapInstanceFactory>,
) {
  const ShareMapUrlModal: React.FC<ShareMapManagerFactoryProps & IntlProps> = ({
    characterLimits,
    generateUrl,
    onChangeExportMapHTMLMode = mode => { },
    onSetMapInfo,
    options = {},
    mapInfo = { title: '', description: '' },
    intl
  }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [shareMaps, setShareMaps] = useState<MapObject[]>([]);
    const [selectedMap, setSelectedMap] = useState<MapObject>({} as MapObject);
    const [newMap, setNewMap] = useState(false);
    const [url, setUrl] = useState('');

    useEffect(() => {
      fetchShareMaps();
    }, []);

    const fetchShareMaps = async () => {
      setIsLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const response = await axios.get(`${host}/api/maps`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        const maps = response.data.body.maps
        setShareMaps(maps);
        console.log("Share maps:");
        console.log(shareMaps);
      } catch (error) {
        console.error('Error fetching shared maps:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const convertTimestamp = (timestamp) => {
      if (timestamp) {
        const utcDate = new Date(timestamp);
        return utcDate.toLocaleString();
      } else {
        return ""
      }
    };

    const handleMapClick = (map) => {
      setSelectedMap(map);
      onSetMapInfo({
        title: map['title'],
        description: map['description']
      });
      onChangeExportMapHTMLMode(map['share_type']);
      setUrl(`${window.location.host}/maps/${map['id']}`);
    }

    const handleBackButtonClick = () => {
      fetchShareMaps();
      setNewMap(false);
      setSelectedMap({} as MapObject);
    }

    const handleSaveButtonClick = async () => {
      await fetchShareMaps();
      setNewMap(false);
      setSelectedMap({} as MapObject);
    }

    const handleNewButtonClick = () => {
      onSetMapInfo({
        title: '',
        description: ''
      });
      onChangeExportMapHTMLMode('');
      setUrl('');
      setNewMap(true);
    }

    return (
      <StyledModal className="dataset-modal" id="dataset-modal">
        {(isLoading) ? 
          <LoadingDialog size={64} message="Retrieving shared maps..."/>
        : 
        (selectedMap && selectedMap.id || newMap )? (
          <div>
            <ShareMapInstance map={selectedMap} mapInfo={mapInfo} options={options} newMap={newMap} onSetMapInfo={onSetMapInfo} onChangeExportMapHTMLMode={onChangeExportMapHTMLMode} generateUrl={generateUrl} mapUrl={url} onBack={handleBackButtonClick} onSave={handleSaveButtonClick}/>
          </div>
        ) : (
          <StyledInnerDiv>
            <StyledTable>
              <thead>
                <tr>
                  <StyledTableCell>Title</StyledTableCell>
                  <StyledTableCell>Description</StyledTableCell>
                  <StyledTableCell>Creation Date</StyledTableCell>
                </tr>
              </thead>
              <tbody>
                <StyledNewMapRow  key="new-map" onClick={() => handleNewButtonClick()}>
                  <StyledTableCell colSpan={3}>+ New map share</StyledTableCell>
                </StyledNewMapRow>
              {shareMaps.map(map => (
                <StyledTableRow key={map.id} onClick={() => handleMapClick(map)}>
                  <StyledTableCell>{map.title}</StyledTableCell>
                  <StyledTableCell>{map.description}</StyledTableCell>
                  <StyledTableCell>{convertTimestamp(map.creation_time)}</StyledTableCell>
                </StyledTableRow>
              ))}
              </tbody>
            </StyledTable>
          </StyledInnerDiv>
        )}
      </StyledModal>
    );
  };

  ShareMapUrlModal.defaultProps = {
    characterLimits: MAP_INFO_CHARACTER,
  }

  return injectIntl(ShareMapUrlModal);
}
